/*
 * SMTP Filename Distribution helper
 * ---------------------------------
 * Customer-facing, self-service tool for site-level SMTP integrations.
 *
 * When every camera at a site emails us from a single site address, the only
 * way to tell the cameras apart is the attachment FILENAME. The backend
 * (frame_receiver_smtp_v2) runs a regular expression against each filename and
 * uses the FIRST capture group as the camera name:
 *
 *     re.search(read_filename_expression, filename).groups()[0]
 *
 * That extracted string must match a camera name registered at the site exactly,
 * or the frame is dropped as UNIDENTIFIED_CAMERA. Support currently sets this up
 * by hand: they read the smtp-frame-receiver logs to find the real filename
 * format, tweak the expression, and confirm it lands on a real camera name.
 * This component automates all three steps so customers can self-serve:
 *   1. surfaces the filenames we've actually received for this site,
 *   2. lets them build/test the expression with live extraction, and
 *   3. validates the extracted value against the site's real camera names.
 *
 * Fields saved (on the `customer` object, via PATCH customer/{id}/):
 *   - read_filename            (bool)  – master on/off
 *   - read_filename_expression (regex) – the pattern
 *
 * NOTE FOR FRONTEND REVIEW: styling here is functional and uses Tailwind + MUI
 * to stay close to the existing SMTP tab, but it has NOT been reconciled with
 * the app's component library / spacing system. Please align to current styling
 * before this ships to customers.
 *
 * BACKEND DEPENDENCY (not in this repo): the "recent filenames" list is fetched
 * from GET customer/{id}/smtp_filenames/, which does not exist yet. It needs to
 * be added to actuate_admin, querying New Relic logs for this site. Contract and
 * suggested NRQL are in the PR description. Until it exists, this component
 * degrades gracefully: the recent-filenames panel shows an explanation and the
 * "test a filename" box still works for manual entry.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { AxiosError, AxiosResponse } from "axios";
import { createApi } from "../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";

interface FilenameSample {
  filename: string;
  extracted_camera_name?: string | null;
  last_seen?: string | null;
  suggested_camera_name?: string | null;
  extracted_matches_existing_camera_id?: number | null;
  suggested_matches_existing_camera_id?: number | null;
}

type RowState = "ok" | "unknown" | "invalid" | "nomatch";

interface EvaluatedRow {
  filename: string;
  extracted: string | null;
  span: [number, number] | null;
  state: RowState;
  nearest: string | null;
  suggestion: string | null;
}

const PRESETS: { label: string; expression: string }[] = [
  { label: "Channel + underscore", expression: "^(ch\\d+)_" },
  { label: "Text before first _", expression: "^([^_]+)_" },
  { label: "Text before the date", expression: "^(.+?)_\\d{8}" },
  { label: "Whole name, without extension", expression: "^(.+)\\.[a-z0-9]+$" },
];

type SiteCameraEntry = {
  camera_name?: string;
  customer?: Record<string, unknown>;
};

/** The camera/site response is either a bare array or a paginated { results: [...] } wrapper. */
const extractCamerasArray = (parsed: unknown): SiteCameraEntry[] | null => {
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray((parsed as { results?: unknown })?.results)) {
    return (parsed as { results: SiteCameraEntry[] }).results;
  }
  return null;
};

/** localStorage.siteCameras holds the paginated camera/site response ({ results: [...] }), not a bare array. */
const getSiteCamerasArray = (): SiteCameraEntry[] => {
  try {
    const parsed = JSON.parse(localStorage.siteCameras || "[]");
    return extractCamerasArray(parsed) ?? [];
  } catch {
    return [];
  }
};

/** Read the site's registered camera names from localStorage (set by camera/site fetch). */
const getSiteCameraNames = (): string[] => {
  const cameras = getSiteCamerasArray();
  return cameras
    .map((camera) => camera?.camera_name)
    .filter((name): name is string => Boolean(name));
};

/** Seed the current saved values from the camera list customer object, if present. */
const getSavedExpressionSeed = (): {
  read_filename: boolean;
  read_filename_expression: string;
} => {
  const cameras = getSiteCamerasArray();
  const customer = cameras?.find((camera) => camera.customer)?.customer;
  return {
    read_filename: Boolean(customer?.read_filename),
    read_filename_expression: (customer?.read_filename_expression as string) || "",
  };
};

const getSiteInfo = (): { id: number | null; name: string } => {
  try {
    const siteInfo = JSON.parse(localStorage.siteInfo || "{}");
    return { id: siteInfo?.id ?? null, name: siteInfo?.name || "this site" };
  } catch {
    return { id: null, name: "this site" };
  }
};

export const SMTPFilenameDistribution = () => {
  const navigate = useNavigate();

  const site = useMemo(getSiteInfo, []);
  const cameraNames = useMemo(getSiteCameraNames, []);
  const cameraNameSet = useMemo(() => new Set(cameraNames), [cameraNames]);
  const seed = useMemo(getSavedExpressionSeed, []);

  const [enabled, setEnabled] = useState<boolean>(seed.read_filename);
  const [expression, setExpression] = useState<string>(
    seed.read_filename_expression
  );
  const [samples, setSamples] = useState<FilenameSample[]>([]);
  const [loadingSamples, setLoadingSamples] = useState<boolean>(true);
  const [samplesError, setSamplesError] = useState<boolean>(false);
  const [manualSamples, setManualSamples] = useState<string[]>([]);
  const [manualInput, setManualInput] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [suggestedExpression, setSuggestedExpression] = useState<string>("");

  // Fetch the filenames we've actually received for this site.
  useEffect(() => {
    setSuggestedExpression("");
    if (site.id == null) {
      setLoadingSamples(false);
      setSamplesError(true);
      return;
    }
    let cancelled = false;
    const AxiosUI = createApi(``);
    AxiosUI.get(`customer/${site.id}/smtp_filenames/`).then(
      (response: AxiosResponse) => {
        if (cancelled) return;
        const data = response.data || {};
        setSamples(Array.isArray(data.samples) ? data.samples : []);
        // Prefer the authoritative saved values from the endpoint when present.
        if (typeof data.read_filename === "boolean") {
          setEnabled(data.read_filename);
        }
        if (typeof data.read_filename_expression === "string") {
          setExpression(data.read_filename_expression);
        }
        if (typeof data.suggested_expression === "string" && data.suggested_expression) {
          setSuggestedExpression(data.suggested_expression);
          // Prefill only when no rule is saved or typed yet.
          if (typeof data.read_filename_expression !== "string" || !data.read_filename_expression) {
            setExpression((previous) => previous || data.suggested_expression);
          }
        }
        setLoadingSamples(false);
      },
      () => {
        if (cancelled) return;
        // Graceful degradation: endpoint not available yet. Manual testing still works.
        setSamplesError(true);
        setLoadingSamples(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [site.id]);

  const sampleByFilename = useMemo(() => {
    const map = new Map<string, FilenameSample>();
    samples.forEach((sample) => map.set(sample.filename, sample));
    return map;
  }, [samples]);

  const nearestCamera = (extracted: string): string | null => {
    const lower = extracted.toLowerCase();
    const collapse = (value: string) =>
      value.toLowerCase().replace(/[\s_-]/g, "");
    for (const name of cameraNames) {
      if (name.toLowerCase() === lower) return name;
    }
    for (const name of cameraNames) {
      if (collapse(name) === collapse(extracted)) return name;
    }
    return null;
  };

  // Compile once per keystroke; "d" flag gives capture-group indices for highlighting.
  const compiled = useMemo(() => {
    if (!expression) return { regex: null as RegExp | null, error: null };
    try {
      return { regex: new RegExp(expression, "d"), error: null };
    } catch (error) {
      return { regex: null, error: (error as Error).message };
    }
  }, [expression]);

  const evaluate = (filename: string): EvaluatedRow => {
    const sample = sampleByFilename.get(filename);
    const suggestion =
      sample?.suggested_camera_name &&
      sample?.suggested_matches_existing_camera_id != null
        ? sample.suggested_camera_name
        : null;
    if (compiled.error || !compiled.regex) {
      return {
        filename,
        extracted: null,
        span: null,
        state: "invalid",
        nearest: null,
        suggestion: null,
      };
    }
    const match = compiled.regex.exec(filename);
    if (!match || match[1] === undefined) {
      return {
        filename,
        extracted: null,
        span: null,
        state: "nomatch",
        nearest: null,
        suggestion,
      };
    }
    const extracted = match[1];
    // indices is available with the "d" flag on modern browsers.
    const indices = (match as unknown as { indices?: Array<[number, number]> })
      .indices;
    const span = indices && indices[1] ? indices[1] : null;
    if (cameraNameSet.has(extracted)) {
      return {
        filename,
        extracted,
        span,
        state: "ok",
        nearest: null,
        suggestion: null,
      };
    }
    // No cached camera list to validate against, so fall back to the backend's
    // own match — server-confirmed only, not cross-checked against the local roster.
    if (
      cameraNameSet.size === 0 &&
      sample?.extracted_matches_existing_camera_id != null &&
      sample?.extracted_camera_name === extracted
    ) {
      return {
        filename,
        extracted,
        span,
        state: "ok",
        nearest: null,
        suggestion: null,
      };
    }
    return {
      filename,
      extracted,
      span,
      state: "unknown",
      nearest: nearestCamera(extracted),
      suggestion,
    };
  };

  const testFilenames = useMemo(() => {
    const fromSamples = samples.map((sample) => sample.filename);
    // manual entries first so they're easy to see, then de-dupe.
    return Array.from(new Set([...manualSamples, ...fromSamples]));
  }, [samples, manualSamples]);

  const rows = useMemo(
    () => testFilenames.map(evaluate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testFilenames, compiled, cameraNameSet, sampleByFilename]
  );

  const expressionChips = useMemo(() => {
    if (!suggestedExpression) return PRESETS;
    return [
      { label: "Suggested for this site", expression: suggestedExpression },
      ...PRESETS.filter((preset) => preset.expression !== suggestedExpression),
    ];
  }, [suggestedExpression]);

  const counts = useMemo(() => {
    let ok = 0;
    let unknown = 0;
    let unmatched = 0;
    rows.forEach((row) => {
      if (row.state === "ok") ok += 1;
      else if (row.state === "unknown") unknown += 1;
      else unmatched += 1;
    });
    return { ok, unknown, unmatched, total: rows.length };
  }, [rows]);

  const addManualFilename = () => {
    const value = manualInput.trim();
    if (!value) return;
    setManualSamples((previous) => [value, ...previous]);
    setManualInput("");
  };

  const handleSave = () => {
    if (site.id == null || compiled.error) return;
    setSaving(true);
    setSaved(false);
    const AxiosUI = createApi(``);
    const patchBody = {
      read_filename: enabled,
      read_filename_expression: enabled ? expression : "",
    };
    const retryFunction = () => handleSave();
    AxiosUI.patch(`customer/${site.id}/`, patchBody).then(
      () => {
        setSaving(false);
        setSaved(true);
        // best-effort: keep the cached camera list in sync so a re-open reflects the save.
        try {
          const parsed = JSON.parse(localStorage.siteCameras || "[]");
          const cameras = extractCamerasArray(parsed);
          if (cameras?.some((camera) => camera.customer)) {
            cameras.forEach((camera) => {
              if (camera.customer) {
                camera.customer.read_filename = patchBody.read_filename;
                camera.customer.read_filename_expression =
                  patchBody.read_filename_expression;
              }
            });
            const container = Array.isArray(parsed)
              ? cameras
              : { ...parsed, results: cameras };
            localStorage.setItem("siteCameras", JSON.stringify(container));
          }
        } catch {
          // ignore cache-sync failures
        }
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setSaving(false);
          alert("Could not save the filename rule. Please try again.");
        });
      }
    );
  };

  const highlightFilename = (
    filename: string,
    span: [number, number] | null
  ) => {
    if (!span) return filename;
    const [start, end] = span;
    return (
      <>
        {filename.slice(0, start)}
        <mark className="bg-actuate-orange/20 rounded-sm px-0.5 font-bold text-actuate-blue">
          {filename.slice(start, end)}
        </mark>
        {filename.slice(end)}
      </>
    );
  };

  const statusLabel = (row: EvaluatedRow) => {
    if (row.state === "ok") {
      return <span className="text-green-700">✓ Matches {row.extracted}</span>;
    }
    if (row.state === "unknown") {
      return (
        <span className="text-yellow-700">
          ⚠ No camera named &ldquo;{row.extracted}&rdquo;
          {row.nearest ? ` — did you mean ${row.nearest}?` : " at this site"}
          {row.suggestion && row.suggestion !== row.extracted ? (
            <span className="text-gray-500">
              {" "}
              — the suggested rule reads &ldquo;{row.suggestion}&rdquo;
            </span>
          ) : null}
        </span>
      );
    }
    if (row.state === "invalid") {
      return <span className="text-red-700">Fix the rule</span>;
    }
    return (
      <span className="text-red-700">
        ✗ No camera identified
        {/* No dedupe-vs-extracted check needed here: extracted is always null for nomatch rows. */}
        {row.suggestion ? (
          <span className="text-gray-500">
            {" "}
            — the suggested rule reads &ldquo;{row.suggestion}&rdquo;
          </span>
        ) : null}
      </span>
    );
  };

  return (
    <div className="mx-6 mt-8 pb-6 border-t border-gray-200 pt-6 font-mulish">
      <h3 className="font-bold text-lg text-actuate-blue">
        Reading camera names from email filenames
      </h3>
      <p className="mt-2 max-w-2xl text-sm text-gray-600">
        If all of your cameras email us from a single site address, we use each
        image&rsquo;s <strong>filename</strong> to tell which camera it came
        from. Set a rule that pulls the camera name out of the filename, then
        check it against filenames we&rsquo;ve actually received. The
        highlighted part is what we&rsquo;ll use as the camera name — it must
        match one of your cameras exactly.
      </p>

      {/* Enable toggle */}
      <div className="mt-4 flex items-center gap-2">
        <Switch
          checked={enabled}
          onChange={(event) => {
            setEnabled(event.target.checked);
            setSaved(false);
          }}
        />
        <div>
          <div className="font-bold text-sm">
            Identify cameras from the filename
          </div>
          <div className="text-xs text-gray-500">
            When off, images from this site arrive unlabeled and can&rsquo;t be
            routed to a camera.
          </div>
        </div>
      </div>

      {enabled && (
        <div className="mt-5">
          {/* Expression field */}
          <div className="block text-xs font-bold uppercase tracking-wide text-gray-500">
            Filename rule
          </div>
          <TextField
            fullWidth
            value={expression}
            onChange={(event) => {
              setExpression(event.target.value);
              setSaved(false);
            }}
            error={Boolean(compiled.error)}
            helperText={
              compiled.error
                ? `That's not a valid pattern yet: ${compiled.error}`
                : "The text captured by the first ( ) group becomes the camera name."
            }
            placeholder="e.g. ^(ch\\d+)_"
            inputProps={{
              style: { fontFamily: "monospace" },
              spellCheck: false,
            }}
            sx={{ mt: 1, maxWidth: 520 }}
            size="small"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 self-center">
              Not sure? Start from:
            </span>
            {expressionChips.map((chip) => {
              const isSuggested = chip.expression === suggestedExpression;
              const isSelected = expression === chip.expression;
              return (
                <button
                  key={chip.expression}
                  type="button"
                  onClick={() => {
                    setExpression(chip.expression);
                    setSaved(false);
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-mono transition ${
                    isSelected
                      ? "border-actuate-blue bg-actuate-blue/10 text-actuate-blue"
                      : isSuggested
                      ? "border-actuate-orange text-actuate-orange hover:border-actuate-blue"
                      : "border-gray-300 text-gray-600 hover:border-actuate-blue"
                  }`}
                  title={chip.expression}
                >
                  <span className="font-mulish font-bold mr-2">
                    {chip.label}
                  </span>
                  {chip.expression}
                </button>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-5 flex gap-6 rounded-lg border border-gray-200 p-3 text-center">
            <div className="flex-1">
              <div className="text-2xl font-bold text-green-700">
                {counts.ok}
                <span className="text-sm font-semibold text-gray-400">
                  /{counts.total}
                </span>
              </div>
              <div className="text-xs text-gray-500">route to a camera</div>
            </div>
            <div className="flex-1">
              <div
                className={`text-2xl font-bold ${
                  counts.unknown ? "text-yellow-700" : "text-gray-400"
                }`}
              >
                {counts.unknown}
              </div>
              <div className="text-xs text-gray-500">unknown camera name</div>
            </div>
            <div className="flex-1">
              <div
                className={`text-2xl font-bold ${
                  counts.unmatched ? "text-red-700" : "text-gray-400"
                }`}
              >
                {counts.unmatched}
              </div>
              <div className="text-xs text-gray-500">no name found</div>
            </div>
          </div>

          {/* Recent filenames */}
          <div className="mt-5">
            <div className="flex items-baseline justify-between">
              <div className="block text-xs font-bold uppercase tracking-wide text-gray-500">
                Recent filenames received
              </div>
              {!loadingSamples && !samplesError && (
                <span className="text-xs text-gray-400">
                  Last seen by the mail receiver
                </span>
              )}
            </div>

            {loadingSamples && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                <CircularProgress size={16} /> Looking up recent filenames…
              </div>
            )}

            {samplesError && !loadingSamples && (
              <div className="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
                We couldn&rsquo;t load recent filenames for this site right now.
                You can still paste a filename below to test your rule.
              </div>
            )}

            {!loadingSamples && !samplesError && samples.length === 0 && (
              <div className="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
                No recent filenames received yet. Once your cameras start
                emailing us, they&rsquo;ll appear here. You can paste a filename
                below to test your rule in the meantime.
              </div>
            )}

            {/* Manual test input */}
            <div className="mt-3 flex gap-2">
              <TextField
                value={manualInput}
                onChange={(event) => setManualInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") addManualFilename();
                }}
                placeholder="Paste a filename to test, e.g. ch03_20260713_2227_E.jpg"
                size="small"
                sx={{ flex: 1, maxWidth: 520 }}
                inputProps={{ style: { fontFamily: "monospace" } }}
              />
              <Button variant="outlined" onClick={addManualFilename}>
                Test
              </Button>
            </div>

            {rows.length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2 font-bold">Filename received</th>
                      <th className="px-3 py-2 font-bold">
                        Camera name we&rsquo;d read
                      </th>
                      <th className="px-3 py-2 font-bold">Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={`${row.filename}-${index}`}
                        className="border-t border-gray-100"
                      >
                        <td className="px-3 py-2 font-mono text-xs text-gray-600 break-all">
                          {highlightFilename(row.filename, row.span)}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs font-bold">
                          {row.extracted ?? (
                            <span className="font-sans font-normal italic text-gray-400">
                              nothing matched
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs font-semibold">
                          {statusLabel(row)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Registered cameras reference */}
            {cameraNames.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  Cameras registered at this site
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {cameraNames.map((name) => (
                    <span
                      key={name}
                      className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs text-gray-600"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save */}
      <div className="mt-6 flex items-center gap-3">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || Boolean(compiled.error) || site.id == null}
          sx={{
            backgroundColor: "#FF8400",
            "&:hover": { backgroundColor: "#e67700" },
          }}
        >
          {saving ? "Saving…" : "Save rule"}
        </Button>
        {saved && <span className="text-sm text-green-700">Saved.</span>}
        {enabled &&
          !compiled.error &&
          counts.total > 0 &&
          counts.ok < counts.total && (
            <span className="text-sm text-gray-500">
              {counts.total - counts.ok} recent filename
              {counts.total - counts.ok > 1 ? "s" : ""} won&rsquo;t route yet.
            </span>
          )}
      </div>
    </div>
  );
};
